import {
  RefreshButton,
  RefreshIcon1,
} from '@euler/app/components/RefreshButton';
import {
  StatusBadge,
  StatusBadgeTarget,
} from '@euler/app/components/StatusBadge';
import { IconProps } from '@euler/app/components/icons/types';
import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { WiredBackButton } from '@euler/app/tv/components/BackButton';
import { MediaIcon } from '@euler/app/tv/components/detail/icons/MediaIcon';
import { PlanIcon } from '@euler/app/tv/components/detail/icons/PlanIcon';
import { ProgressIcon } from '@euler/app/tv/components/detail/icons/ProgressIcon';
import { ScanIcon } from '@euler/app/tv/components/detail/icons/ScanIcon';
import { ShareIcon } from '@euler/app/tv/components/detail/icons/ShareIcon';
import { kTaskDetailHeaderHeight } from '@euler/app/tv/functions/constants';
import { useCommonTaskHeader } from '@euler/app/tv/functions/useCommonTaskHeader';
import { useScreenContentPadding } from '@euler/app/tv/functions/utils';
import { usePresentationTheme } from '@euler/app/tv/theme';
import { PresentationTheme } from '@euler/app/tv/theme/types';
import { Colors } from '@euler/components/Colors';
import { Icon as IcomoonIcon } from '@euler/components/Icomoon';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { getTaskStatusFromTaskDetail } from '@euler/model';
import { TaskDetail, TaskExtraInfo } from '@euler/model/task-detail';
import { FC, memo, useMemo } from 'react';
import { StyleProp, TouchableOpacity, View, ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const kStatusBadgeSize = 42;
const kButtonSize = 75;

const ActionButton = memo(
  ({
    theme,
    label,
    style,
    Icon,
    onPress,
  }: {
    theme: PresentationTheme;
    label: string;
    value?: number;
    style?: StyleProp<ViewStyle>;
    Icon: FC<IconProps>;
    onPress?: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          align-items: center;
          justify-content: center;
          border-width: 0px;
          border-color: ${Colors.White4};
          margin-left: ${rpx(50)}px;
        `}
        style={style}
        onPress={onPress}
      >
        <View
          css={`
            align-items: center;
            justify-content: center;
            width: ${rpx(kButtonSize)}px;
            height: ${rpx(kButtonSize)}px;
            border-radius: ${rpx(kButtonSize) / 2}px;
            background-color: ${theme.task.navbar.action.bgColor};
          `}
        >
          <Icon color={theme.task.navbar.action.iconColor} size={rpx(40)} />
        </View>

        <Typography.Label
          regular
          size={rpx(20)}
          lineHeight={rpx(36)}
          color={theme.task.navbar.action.textColor}
        >
          {label}
        </Typography.Label>
      </TouchableOpacity>
    );
  },
);

const BackIcon: FC<IconProps> = memo(props => {
  return (
    <IcomoonIcon name="chevron-left" size={props.size} color={props.color} />
  );
});

export const HeaderView = memo(
  ({
    detail,
    targetStatus,
    style,
    onRefresh,
    onSharePress,
    onSubscribeBindPress,
    onMediasPress,
    onMaintenancePlanPress,
    onTimelinePress,
    onGoBack,
  }: {
    detail: TaskDetail;
    extraInfo?: TaskExtraInfo;
    targetStatus?: StatusBadgeTarget;
    style?: StyleProp<ViewStyle>;
    onRefresh?: () => void;
    onSharePress?: () => void;
    onSubscribeBindPress?: () => void;
    onMediasPress?: () => void;
    onMaintenancePlanPress?: () => void;
    onTimelinePress?: () => void;
    onGoBack?: () => void;
  }) => {
    const theme = usePresentationTheme();
    const { rpx } = useResponsiveContext();

    const statusInfo = useMemo(
      () => getTaskStatusFromTaskDetail(detail),
      [detail],
    );

    return (
      <View
        css={`
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
        `}
        style={style}
      >
        <View
          css={`
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;
          `}
        >
          <WiredBackButton color={theme.task.navbar.backButtonColor} />
          <Typography.Label
            bold
            size={rpx(64)}
            color={theme.task.navbar.licensePlateNoTextColor}
            light
            css={`
              margin-left: ${rpx(40)}px;
            `}
          >
            {detail.basicInfo.licensePlateNo}
          </Typography.Label>
          <StatusBadge
            statusInfo={statusInfo}
            targetStatus={targetStatus}
            bordered
            theme={theme.statusBadge}
            textStyle={{
              fontSize: rpx(28),
              lineHeight: rpx(kStatusBadgeSize) - 2,
            }}
            css={`
              height: ${rpx(kStatusBadgeSize)}px;
              border-radius: ${rpx(8)}px;
              padding: 0 ${rpx(12)}px;
              margin-left: ${rpx(41)}px;
            `}
          />
          <RefreshButton
            onPress={onRefresh}
            iconProps={{ size: rpx(27), color: Colors.White3 }}
            css={`
              width: ${rpx(35)}px;
              height: ${rpx(35)}px;
              margin-left: ${rpx(41)}px;
              opacity: 0;
            `}
          />
        </View>
        <HStack
          justifyContent={'flex-end'}
          css={`
            margin-left: ${rpx(8)}px;
            flex: 1;
          `}
        >
          <ActionButton
            Icon={RefreshIcon1}
            theme={theme}
            label="刷新"
            onPress={onRefresh}
          />
          <ActionButton
            Icon={ShareIcon}
            theme={theme}
            label="分享"
            onPress={onSharePress}
          />
          <ActionButton
            Icon={ScanIcon}
            theme={theme}
            label="关注"
            onPress={onSubscribeBindPress}
          />
          <ActionButton
            Icon={MediaIcon}
            theme={theme}
            label="图库"
            onPress={onMediasPress}
          />
          <ActionButton
            Icon={PlanIcon}
            theme={theme}
            label="维保计划"
            onPress={onMaintenancePlanPress}
          />
          <ActionButton
            Icon={ProgressIcon}
            theme={theme}
            label="进度"
            onPress={onTimelinePress}
          />
          <ActionButton
            Icon={BackIcon}
            theme={theme}
            label="返回"
            onPress={onGoBack}
          />
        </HStack>
      </View>
    );
  },
);

export const WiredHeaderView = memo(
  ({ targetStatus }: { targetStatus?: StatusBadgeTarget }) => {
    const { top } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { detail } = useTaskContext();
    const extraInfo = useTaskExtraInfo();
    const [paddingLeft, paddingRight] = useScreenContentPadding();
    const {
      onRefresh,
      onMediasPress,
      onMaintenancePlanPress,
      onTimelinePress,
      onSubscribeBindPress,
      onShare,
      onGoBack,
    } = useCommonTaskHeader();

    return (
      <HeaderView
        extraInfo={extraInfo}
        detail={detail}
        targetStatus={targetStatus}
        onRefresh={onRefresh}
        onMediasPress={onMediasPress}
        onMaintenancePlanPress={onMaintenancePlanPress}
        onSharePress={onShare}
        onGoBack={onGoBack}
        onTimelinePress={onTimelinePress}
        onSubscribeBindPress={onSubscribeBindPress}
        css={`
          height: ${rpx(kTaskDetailHeaderHeight) + top}px;
          padding-left: ${paddingLeft}px;
          padding-right: ${paddingRight}px;
        `}
      />
    );
  },
);
